/**
 * VacationRequestDetailModal - Request detail modal
 * Story: VAC-DS-03
 */

import React from 'react'
import { X } from 'lucide-react'
import type { VacationRequest } from '@/types/vacation'
import { formatDateRange } from '@/lib/utils/date-utils'

interface VacationRequestDetailModalProps {
  request: VacationRequest | null
  open: boolean
  onClose: () => void
  onCancel?: (requestId: string) => void
}

const getStatusClass = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'status-primary'
    case 'PENDING':
      return 'status-warning'
    case 'REJECTED':
      return 'status-error'
    case 'CANCELLED':
      return 'bg-brand-dark-light text-brand-gray'
    default:
      return 'bg-brand-dark-light text-brand-gray'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'Aprovado'
    case 'PENDING':
      return 'Pendente'
    case 'REJECTED':
      return 'Rejeitado'
    case 'CANCELLED':
      return 'Cancelado'
    default:
      return status
  }
}

export const VacationRequestDetailModal: React.FC<VacationRequestDetailModalProps> = ({
  request,
  open,
  onClose,
  onCancel
}) => {
  if (!open || !request) return null

  const canCancel = request.status === 'PENDING' && onCancel

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="modal-content w-full max-w-lg pointer-events-auto animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-display font-bold text-brand-primary">
              Detalhes da Solicitação
            </h2>
            <button
              onClick={onClose}
              className="btn-icon btn-ghost"
              aria-label="fechar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="space-y-4">
            {/* Request ID */}
            <div>
              <label className="text-xs text-brand-gray font-body block mb-1">
                ID da Solicitação
              </label>
              <p className="text-base text-white font-mono">{request.requestId}</p>
            </div>

            <div className="border-t border-brand-dark-light my-4"></div>

            {/* Status */}
            <div>
              <label className="text-xs text-brand-gray font-body block mb-2">
                Status
              </label>
              <span className={`inline-block px-3 py-1 rounded-monumenta text-xs font-display font-semibold ${getStatusClass(request.status)}`}>
                {getStatusLabel(request.status)}
              </span>
            </div>

            {/* Period */}
            <div>
              <label className="text-xs text-brand-gray font-body block mb-1">
                Período
              </label>
              <p className="text-base text-white font-display">
                {formatDateRange(request.startDate, request.endDate)}
              </p>
            </div>

            {/* Days */}
            <div>
              <label className="text-xs text-brand-gray font-body block mb-1">
                Dias
              </label>
              <p className="text-base text-white font-display font-semibold">
                {request.totalDays}
              </p>
            </div>

            {/* Notes */}
            {request.notes && (
              <div>
                <label className="text-xs text-brand-gray font-body block mb-1">
                  Observações
                </label>
                <p className="text-sm text-white font-body">
                  {request.notes}
                </p>
              </div>
            )}

            {/* Rejection Reason */}
            {request.rejectionReason && (
              <div>
                <label className="text-xs text-brand-gray font-body block mb-1">
                  Motivo da Rejeição
                </label>
                <p className="text-sm text-error font-body">
                  {request.rejectionReason}
                </p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-brand-dark-light">
            {canCancel && (
              <button
                onClick={() => {
                  onCancel(request.requestId)
                  onClose()
                }}
                className="px-4 py-2 rounded-monumenta font-display text-sm text-error hover:bg-error/10 transition-colors"
              >
                Cancelar Solicitação
              </button>
            )}
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
